import React, { useState, useEffect, useCallback, useRef } from 'react';
import { hasPermission } from '../utils/permissionUtils';
import { useLanguage } from '../contexts/LanguageContext';
import { logActivity } from '../utils/activityLogger';
import ManageGallery from './ManageGallery';
import '../styles/gallery.css';

const Gallery = ({ user, isOpen: isInitiallyOpen = true }) => {
  const { t } = useLanguage();
  const [galleryItems, setGalleryItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isOpen, setIsOpen] = useState(isInitiallyOpen);
  const [showManageDialog, setShowManageDialog] = useState(false);
  
  // Carousel state
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [showLightbox, setShowLightbox] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  
  // Filters
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [showFeaturedOnly, setShowFeaturedOnly] = useState(false);
  
  const autoPlayRef = useRef(null);
  const touchStartRef = useRef(null);
  const touchEndRef = useRef(null);

  const canManageGallery = hasPermission(user, 'manage-gallery');

  const fetchGalleryItems = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
      const params = new URLSearchParams({
        is_public: 'true',
        sortBy: 'created_at',
        sortOrder: 'desc',
        limit: '50' // Get more items for carousel
      });

      if (categoryFilter !== 'all') params.append('category', categoryFilter);
      if (showFeaturedOnly) params.append('featured', 'true');
      
      logActivity('gallery_filter', 'gallery', null, 
        `User filtered gallery by category: ${categoryFilter}, featured: ${showFeaturedOnly}`,
        { categoryFilter, showFeaturedOnly });

      const response = await fetch(`${API_BASE}/api/gallery?${params}`);
      
      if (!response.ok) {
        throw new Error(t('gallery.loadErrors.failedToFetch'));
      }

      const data = await response.json();
      setGalleryItems(data.items || []);
      setCurrentIndex(0); // Reset to first item when filters change
    } catch (err) {
      console.error('Error fetching gallery items:', err);
      setError(t('gallery.loadErrors.failedToLoad'));
    } finally {
      setLoading(false);
    }
  }, [categoryFilter, showFeaturedOnly]);

  const handleManageClose = useCallback(() => {
    setShowManageDialog(false);
    // Force refresh by clearing items first, then fetching
    setGalleryItems([]);
    setTimeout(() => {
      fetchGalleryItems();
    }, 100);
  }, [fetchGalleryItems]);

  useEffect(() => {
    fetchGalleryItems();
  }, [fetchGalleryItems]);

  // Auto-play functionality
  useEffect(() => {
    if (isAutoPlaying && galleryItems.length > 1 && isOpen) {
      autoPlayRef.current = setInterval(() => {
        setCurrentIndex(prev => (prev + 1) % galleryItems.length);
      }, 5000); // Change every 5 seconds
    } else {
      clearInterval(autoPlayRef.current);
    }

    return () => clearInterval(autoPlayRef.current);
  }, [isAutoPlaying, galleryItems.length, isOpen]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen || galleryItems.length === 0) return;
      
      switch (e.key) {
        case 'ArrowLeft':
          // Only prevent default if not typing in a text input
          if (document.activeElement.tagName !== 'INPUT' && 
              document.activeElement.tagName !== 'TEXTAREA' && 
              !document.activeElement.isContentEditable) {
            e.preventDefault();
            goToPrevious();
          }
          break;
        case 'ArrowRight':
          // Only prevent default if not typing in a text input
          if (document.activeElement.tagName !== 'INPUT' && 
              document.activeElement.tagName !== 'TEXTAREA' && 
              !document.activeElement.isContentEditable) {
            e.preventDefault();
            goToNext();
          }
          break;
        case ' ':
          // Only prevent default if not typing in a text input
          if (document.activeElement.tagName !== 'INPUT' && 
              document.activeElement.tagName !== 'TEXTAREA' && 
              !document.activeElement.isContentEditable) {
            e.preventDefault();
            setIsAutoPlaying(prev => !prev);
          }
          break;
        case 'Escape':
          if (showLightbox) {
            setShowLightbox(false);
          }
          break;
        default:
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, galleryItems.length, showLightbox]);

  const goToNext = () => {
    const newIndex = (currentIndex + 1) % galleryItems.length;
    logActivity('gallery_navigate', 'gallery', galleryItems[newIndex]?.id, 
      'User navigated to next gallery item',
      { direction: 'next', fromIndex: currentIndex, toIndex: newIndex });
    setCurrentIndex(newIndex);
    setIsAutoPlaying(false); // Pause auto-play when user manually navigates
  };

  const goToPrevious = () => {
    const newIndex = (currentIndex - 1 + galleryItems.length) % galleryItems.length;
    logActivity('gallery_navigate', 'gallery', galleryItems[newIndex]?.id, 
      'User navigated to previous gallery item',
      { direction: 'previous', fromIndex: currentIndex, toIndex: newIndex });
    setCurrentIndex(newIndex);
    setIsAutoPlaying(false);
  };

  const goToSlide = (index) => {
    logActivity('gallery_navigate', 'gallery', galleryItems[index]?.id, 
      'User navigated directly to gallery item',
      { direction: 'direct', fromIndex: currentIndex, toIndex: index });
    setCurrentIndex(index);
    setIsAutoPlaying(false);
  };

  const openLightbox = (index) => {
    logActivity('gallery_lightbox_open', 'gallery', galleryItems[index]?.id, 
      'User opened gallery lightbox',
      { itemIndex: index, itemTitle: galleryItems[index]?.title });
    setLightboxIndex(index);
    setShowLightbox(true);
  };

  const closeLightbox = () => {
    setShowLightbox(false);
  };

  const goToNextInLightbox = () => {
    setLightboxIndex(prev => (prev + 1) % galleryItems.length);
  };

  const goToPreviousInLightbox = () => {
    setLightboxIndex(prev => (prev - 1 + galleryItems.length) % galleryItems.length);
  };

  // Touch/swipe handling
  const handleTouchStart = (e) => {
    touchStartRef.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e) => {
    touchEndRef.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (!touchStartRef.current || !touchEndRef.current) return;
    
    const distance = touchStartRef.current - touchEndRef.current;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) {
      goToNext();
    } else if (isRightSwipe) {
      goToPrevious();
    }

    touchStartRef.current = null;
    touchEndRef.current = null;
  };

  const categories = [
    { value: 'all', label: t('gallery.categories.all') },
    { value: 'general', label: t('gallery.categories.general') },
    { value: 'concert', label: t('gallery.categories.concert') },
    { value: 'rehearsal', label: t('gallery.categories.rehearsal') },
    { value: 'social', label: t('gallery.categories.social') },
    { value: 'achievements', label: t('gallery.categories.achievements') },
    { value: 'behind-scenes', label: t('gallery.categories.behindScenes') }
  ];

  const currentItem = galleryItems[currentIndex];
  
  return (
    <div className="gallery-section">
      <div className="section-header" onClick={() => setIsOpen(!isOpen)}>
        <div className="section-title">
          <span className="section-icon">🖼️</span>
          <h2>{t('gallery.galleryTitle')}</h2>
          <span className="item-count">({galleryItems.length})</span>
        </div>
        <div className="section-controls">
          {canManageGallery && (
            <button 
              className="manage-button"
              onClick={(e) => {
                e.stopPropagation();
                setShowManageDialog(true);
              }}
              title={t('gallery.manageGallery')}
            >
              ⚙️
            </button>
          )}
          <button className={`collapse-button ${isOpen ? 'open' : 'closed'}`}>
            {isOpen ? '▼' : '▶'}
          </button>
        </div>
      </div>

      {isOpen && (
        <div className="section-content">
          {error && (
            <div className="error-message">
              <span>⚠️ {error}</span>
              <button onClick={fetchGalleryItems}>{t('gallery.retry')}</button>
            </div>
          )}

          {loading && (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <span>{t('gallery.loadingGallery')}</span>
            </div>
          )}

          {!loading && !error && galleryItems.length === 0 && (
            <div className="empty-state">
              <div className="empty-icon">📷</div>
              <h3>{t('gallery.noPhotosYet')}</h3>
              <p>{t('gallery.noPhotosDescription')}</p>
              {canManageGallery && (
                <button 
                  className="primary-button"
                  onClick={() => setShowManageDialog(true)}
                >
                  {t('gallery.addFirstPhoto')}
                </button>
              )}
            </div>
          )}

          {!loading && !error && galleryItems.length > 0 && (
            <>
              {/* Gallery Controls */}
              <div className="gallery-controls">
                <div className="filter-controls">
                  <select 
                    value={categoryFilter}
                    onChange={(e) => setCategoryFilter(e.target.value)}
                    className="category-filter"
                  >
                    {categories.map(cat => (
                      <option key={cat.value} value={cat.value}>{cat.label}</option>
                    ))}
                  </select>
                  
                  <label className="featured-toggle">
                    <input
                      type="checkbox"
                      checked={showFeaturedOnly}
                      onChange={(e) => setShowFeaturedOnly(e.target.checked)}
                    />
                    <span>{t('gallery.featuredOnly')}</span>
                  </label>
                </div>

                <div className="playback-controls">
                  <button 
                    className={`autoplay-button ${isAutoPlaying ? 'playing' : 'paused'}`}
                    onClick={() => setIsAutoPlaying(!isAutoPlaying)}
                    title={isAutoPlaying ? t('gallery.pauseSlideshow') : t('gallery.playSlideshow')}
                  >
                    {isAutoPlaying ? '⏸️' : '▶️'}
                  </button>
                </div>
              </div>

              {/* Main Carousel */}
              <div 
                className="gallery-carousel"
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
              >
                <button 
                  className="nav-button prev"
                  onClick={goToPrevious}
                  disabled={galleryItems.length <= 1}
                >
                  ❮
                </button>

                <div className="carousel-container">
                  <div 
                    className="carousel-track"
                    style={{ transform: `translateX(-${currentIndex * 100}%)` }}
                  >
                    {galleryItems.map((item, index) => (
                      <div 
                        key={item.id}
                        className={`carousel-slide ${index === currentIndex ? 'active' : ''}`}
                      >
                        <div className="slide-image-container">
                          <img 
                            src={item.image_url}
                            alt={item.title}
                            className="slide-image"
                            onClick={() => openLightbox(index)}
                            loading={index === currentIndex ? 'eager' : 'lazy'}
                          />
                          {item.is_featured && (
                            <div className="featured-badge">{t('gallery.featuredBadge')}</div>
                          )}
                        </div>
                        
                        <div className="slide-content">
                          <h3 className="slide-title">{item.title}</h3>
                          {item.caption && (
                            <p className="slide-caption">{item.caption}</p>
                          )}
                          <div className="slide-meta">
                            <span className="slide-category">{item.category}</span>
                            {item.event_date && (
                              <span className="slide-date">
                                {(() => {
                                  const date = new Date(item.event_date);
                                  return isNaN(date.getTime()) ? 'Invalid Date' : date.toLocaleDateString();
                                })()}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <button 
                  className="nav-button next"
                  onClick={goToNext}
                  disabled={galleryItems.length <= 1}
                >
                  ❯
                </button>
              </div>

              {/* Thumbnail Navigation */}
              {galleryItems.length > 1 && (
                <div className="thumbnail-nav">
                  <div className="thumbnail-track">
                    {galleryItems.map((item, index) => (
                      <button
                        key={item.id}
                        className={`thumbnail ${index === currentIndex ? 'active' : ''}`}
                        onClick={() => goToSlide(index)}
                      >
                        <img 
                          src={item.thumbnail_url || item.image_url}
                          alt={item.title}
                          loading="lazy"
                        />
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Progress Indicator */}
              {galleryItems.length > 1 && (
                <div className="progress-indicator">
                  <div className="progress-text">
                    {currentIndex + 1} / {galleryItems.length}
                  </div>
                  <div className="progress-bar">
                    <div 
                      className="progress-fill"
                      style={{ width: `${((currentIndex + 1) / galleryItems.length) * 100}%` }}
                    />
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Lightbox Modal */}
      {showLightbox && galleryItems[lightboxIndex] && (
        <div className="lightbox-overlay" onClick={closeLightbox}>
          <div className="lightbox-container" onClick={(e) => e.stopPropagation()}>
            <button className="lightbox-close" onClick={closeLightbox}>×</button>
            
            <button 
              className="lightbox-nav prev"
              onClick={goToPreviousInLightbox}
              disabled={galleryItems.length <= 1}
            >
              ❮
            </button>

            <div className="lightbox-content">
              <img 
                src={galleryItems[lightboxIndex].image_url}
                alt={galleryItems[lightboxIndex].title}
                className="lightbox-image"
              />
              <div className="lightbox-info">
                <h3>{galleryItems[lightboxIndex].title}</h3>
                {galleryItems[lightboxIndex].caption && (
                  <p>{galleryItems[lightboxIndex].caption}</p>
                )}
                <div className="lightbox-meta">
                  <span>{t('gallery.categoryLabel')} {galleryItems[lightboxIndex].category}</span>
                  {galleryItems[lightboxIndex].event_date && (
                    <span>{t('gallery.dateLabel')} {(() => {
                      const date = new Date(galleryItems[lightboxIndex].event_date);
                      return isNaN(date.getTime()) ? t('gallery.invalidDate') : date.toLocaleDateString();
                    })()}</span>
                  )}
                </div>
              </div>
            </div>

            <button 
              className="lightbox-nav next"
              onClick={goToNextInLightbox}
              disabled={galleryItems.length <= 1}
            >
              ❯
            </button>

            <div className="lightbox-counter">
              {lightboxIndex + 1} / {galleryItems.length}
            </div>
          </div>
        </div>
      )}

      {/* Management Dialog */}
      {showManageDialog && (
        <ManageGallery
          isOpen={showManageDialog}
          onClose={handleManageClose}
          user={user}
        />
      )}
    </div>
  );
};

export default Gallery;